import React from "react";
import { Box, Typography } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";

// Animations
const sparkleAnimation = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.8) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.2) rotate(180deg);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.05);
  }
`;

const floatingAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
`;

// Styled Components
const BingoIconContainer = styled(Box)(({ size }) => ({
  position: "relative",
  width: size,
  height: size,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const BingoBall = styled(Box)(({ size }) => ({
  position: "relative",
  width: size,
  height: size,
  borderRadius: "50%",
  background: `
    linear-gradient(135deg, 
      #8B5CF6 0%, 
      #7C3AED 25%, 
      #6366F1 50%, 
      #3B82F6 75%, 
      #2563EB 100%
    )
  `,
  boxShadow: `
    0 ${size * 0.1}px ${size * 0.25}px rgba(0, 0, 0, 0.3),
    inset 0 ${size * 0.02}px ${size * 0.05}px rgba(255, 255, 255, 0.3),
    inset 0 -${size * 0.02}px ${size * 0.05}px rgba(0, 0, 0, 0.2)
  `,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `${size * 0.05}px solid rgba(255, 255, 255, 0.9)`,
  cursor: "pointer",
  transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
  animation: `${floatingAnimation} 3s ease-in-out infinite`,
  "&:hover": {
    transform: "scale(1.15) translateY(-5px)",
    boxShadow: `
      0 ${size * 0.2}px ${size * 0.4}px rgba(0, 0, 0, 0.4),
      0 0 ${size * 0.3}px rgba(139, 92, 246, 0.4),
      inset 0 ${size * 0.02}px ${size * 0.05}px rgba(255, 255, 255, 0.4)
    `,
    animation: "none",
  },
  "&:active": {
    transform: "scale(1.05)",
    transition: "all 0.1s ease",
  },
}));

const BallHighlight = styled(Box)(({ size }) => ({
  position: "absolute",
  top: size * 0.1,
  left: size * 0.15,
  width: size * 0.35,
  height: size * 0.35,
  borderRadius: "50%",
  background: `
    radial-gradient(
      ellipse at center,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.3) 40%,
      transparent 70%
    )
  `,
  filter: "blur(1px)",
}));

const LetterB = styled(Typography)(({ size }) => ({
  color: "white",
  fontWeight: 900,
  fontSize: size * 0.45,
  fontFamily: '"Roboto", "Arial Black", sans-serif',
  position: "relative",
  zIndex: 10,
  textShadow: `
    0 ${size * 0.02}px ${size * 0.04}px rgba(0, 0, 0, 0.5),
    0 0 ${size * 0.08}px rgba(255, 255, 255, 0.3)
  `,
  userSelect: "none",
}));

const Sparkle = styled(Box)(({ size, delay = 0 }) => ({
  position: "absolute",
  width: size * 0.08,
  height: size * 0.08,
  borderRadius: "50%",
  backgroundColor: "#E0E7FF",
  boxShadow: `0 0 ${size * 0.1}px rgba(191, 219, 254, 0.9)`,
  animation: `${sparkleAnimation} 2s infinite`,
  animationDelay: `${delay}s`,
  opacity: 0.8,
}));

const Sparkle1 = styled(Sparkle)(({ size }) => ({
  top: size * 0.12,
  right: size * 0.18,
}));

const Sparkle2 = styled(Sparkle)(({ size }) => ({
  top: size * 0.25,
  right: size * 0.08,
}));

const Sparkle3 = styled(Sparkle)(({ size }) => ({
  bottom: size * 0.15,
  left: size * 0.12,
}));

const GlowEffect = styled(Box)(({ size }) => ({
  position: "absolute",
  top: -size * 0.1,
  left: -size * 0.1,
  width: size * 1.2,
  height: size * 1.2,
  borderRadius: "50%",
  background: `
    radial-gradient(
      circle,
      rgba(139, 92, 246, 0.3) 0%,
      rgba(99, 102, 241, 0.2) 50%,
      transparent 70%
    )
  `,
  filter: "blur(10px)",
  zIndex: -1,
  animation: `${glowPulse} 3s infinite`,
}));

const RimGlow = styled(Box)(({ size }) => ({
  position: "absolute",
  top: -size * 0.05,
  left: -size * 0.05,
  width: size * 1.1,
  height: size * 1.1,
  borderRadius: "50%",
  border: `2px solid rgba(147, 51, 234, 0.4)`,
  opacity: 0.6,
  zIndex: -1,
}));

// Main Component
export function BingoIcon({ 
  size = 60, 
  className = "", 
  onClick,
  disabled = false,
  ...props 
}) {
  const handleClick = (e) => {
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  return (
    <BingoIconContainer 
      size={size} 
      className={className} 
      onClick={handleClick}
      {...props}
    >
      <GlowEffect size={size} />
      <RimGlow size={size} />
      <BingoBall 
        size={size} 
        sx={{ 
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          '&:hover': disabled ? {} : undefined
        }}
      >
        <BallHighlight size={size} />
        <LetterB size={size} variant="h1" component="span">
          B
        </LetterB>
        <Sparkle1 size={size} delay={0} />
        <Sparkle2 size={size} delay={0.5} />
        <Sparkle3 size={size} delay={1} />
      </BingoBall>
    </BingoIconContainer>
  );
}
